import React, { useEffect, useMemo } from "react";

import { useEventTracker } from "@/hooks/useEventTracker";

import OpenInNewIcon from "@/components/Icons/open_in_new.svg";

interface DoctorAffiliateAd {
  backgroundColor: string;
  bodyHTML: string;
  clickthroughURL: string;
  clientName: string;
  logoImageUrl: string;
  title: string;
  url: string;
}

/**
 * As of summer 2025, we're expanding the existing Leafwell Doctor Finder ad
 * to a rotating placement between Leafwell and NuggMD.
 *
 * These placements are hardcoded while we determine if:
 * 1) There's customer interest in the placement
 * 2) There are enough impressions and clicks to support more than one ad
 *
 * The intention is NOT to expand this hardcoded list indefinitely. If we need
 * to include more than 2 ads in this placement, we should consider expanding
 * our existing Inventory Manager to support share-of-voice placements and
 * moving these ad units there.
 */
const ads: DoctorAffiliateAd[] = [
  {
    backgroundColor: "#333333",
    bodyHTML: 'Code: <span class="font-bold"> LEAFLY20</span>',
    clickthroughURL:
      "https://leafwell.com/medical-card?utm_source=leafly&utm_medium=doctorad",
    clientName: "Leafwell",
    logoImageUrl:
      "https://leafly-public.imgix.net/web-web/finder/ef1a149a770e_Leafwell_FinderLogo.svg",
    title: "Save 20% on your medical marijuana card with Leafwell",
    url: "leafwell.com",
  },
  {
    backgroundColor: "#103764",
    bodyHTML: 'Code: <span class="font-bold">LEAFLY20</span>',
    clickthroughURL:
      "https://www.mdfromhome.com/select-your-location?utm_source=leafly",
    clientName: "NuggMD",
    logoImageUrl:
      "https://leafly-public.imgix.net/web-web/finder/73e653f24492_NuggMD_FinderLogo.png",
    title: "Save 20% on medical cannabis evals in US, UK, and Germany",
    url: "nuggmd.com",
  },
];

//generate a random number once to determine which ad to show on load
const random = Math.random();

const DoctorAd = () => {
  const { publishEvent } = useEventTracker();

  const selectedAd = useMemo(() => {
    const adIndex = Math.floor(random * ads.length);
    return ads[adIndex];
  }, []);

  useEffect(() => {
    if (selectedAd) {
      publishEvent({
        action: "impression",
        category: `${selectedAd.clientName} Banner`,
        label: "doctor finder",
      });
    }
  }, [selectedAd]);

  if (!selectedAd) {
    return null;
  }

  return (
    <a
      href={selectedAd.clickthroughURL}
      rel="noreferrer"
      target="_blank"
      onClick={() =>
        publishEvent({
          action: "click",
          category: `${selectedAd.clientName} Banner`,
          label: "doctor finder",
        })
      }
    >
      <div
        className="rounded p-lg text-white flex flex-col justify-between cursor-pointer my-lg"
        style={{ backgroundColor: selectedAd.backgroundColor }}
      >
        <h3 className="mb-sm" data-testid="doctor-finder-ad-headline">
          {selectedAd.title}
        </h3>
        <div dangerouslySetInnerHTML={{ __html: selectedAd.bodyHTML }} />
        <div className="flex flex-row items-center justify-between">
          <div className="font-bold text-xs flex flex-row items-center ">
            At <span className="underline mx-xs ">{selectedAd.url}</span>
            <OpenInNewIcon width="14" height="14" />
          </div>
          <img
            src={selectedAd.logoImageUrl}
            alt=""
            className="max-w-half max-h-6 object-contain"
          />
        </div>
      </div>
    </a>
  );
};

export default DoctorAd;
